import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import { Loader } from '../components/loader';
import UpdateHistoryList from './updateHistoryList';
import UpdateStockDetails from './updateStockDetails';
import CCMUpdateHistory from './ccmUpdateHistory';
import './ccmHome.css';
import { formatCycleInfoDate, toCamelCase, getCalendarInfo, parseAndNormalizeDate, parseAndNormalizeEndOfDay } from '../helper-functions';
import { get } from '../config/api';
import { TextField } from '../components/inputControls/textField';
import { CCM_HOME_TITLES, CCM_HOME_MESSAGES } from '../constants/textconstants';
import { UserDetails } from '../interfaces/types';

export const CCMHome: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showHistoryPage, setShowHistoryPage] = useState<boolean>(false);
  const [showUpdateStockPage, setShowUpdateStockPage] = useState<boolean>(false);
  const [showCCMUpdateHistory, setShowCCMUpdateHistory] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<'All' | 'Pending' | 'Updated'>('All');
  const [selectedRetailerStockData, setSelectedRetailerStockData] = useState<any[]>([]);
  const [selectedRetailerHistoryData, setSelectedRetailerHistoryData] = useState<any[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<string>('');
  const [selectedRetailerInfo, setSelectedRetailerInfo] = useState<{customerName: string, customerId: string} | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [ccmHistoryData, setCcmHistoryData] = useState<any[]>([]);
  const [salesDashboardData, setSalesDashboardData] = useState<any[]>([]);
  const [allUpdateHistory, setAllUpdateHistory] = useState<any[]>([]);
  const [selectedEntryFromCCM, setSelectedEntryFromCCM] = useState<any>(null);
  const [customersWithHistory, setCustomersWithHistory] = useState<Set<string>>(new Set());
  const [productsData, setProductsData] = useState<any[]>([]); // Store products with display names
  
  const calendarInfo = getCalendarInfo();

  const fetchUserDetails = async (): Promise<UserDetails> => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('userId')?.toUpperCase();

      if (userId) {
        // First try to fetch user details from API based on userId
        try {
          let response = await get(`/api/v1/customers/${userId}`);
          if (response && (Array.isArray(response) ? response.length > 0 : response)) {
            
            // Filter the response to find the user that matches the userId
            const apiUserData = Array.isArray(response) 
              ? (response.find((user: any) => user.userId === userId) || response[0])
              : response;
            
            // Return user details from API response
            return {
              id: apiUserData.id || userId,
              name: apiUserData.name || 'Unknown',
              classification: apiUserData.classification || 'Unknown',
              subClassification: apiUserData.subClassification || 'Unknown',
            };
          }
        } catch (apiError) {
          console.warn('Failed to fetch user details from API, falling back to URL params:', apiError);
        }

        // Fallback to URL parameters if API fails
        const userDetails: UserDetails = {
          id: 'Unknown',
          name: 'Unknown',
          classification:  'Unknown',
          subClassification: 'Unknown',
        };
        return userDetails;
      }
      console.log('No userId in query string, using fallback');
    } catch (error) {
      console.error('Error in fetchUserDetails:', error);
    }
    
    // Final fallback if no userId is available
    const fallbackUser: UserDetails = {
      id: 'Unknown',
      name: 'Unknown',
      classification:  'Unknown',
      subClassification: 'Unknown',
    };
    console.log('Using fallback user details:', fallbackUser);
    return fallbackUser;
  };

  // Function to determine alert message based on grace period from API response
  const getAlertMessage = () => {

    const currentCycleStartDate = calendarInfo?.currentCycleStartDate;
    const currentCycleDueDate = calendarInfo?.currentCycleDueDate;
    const nextCycleStartDate = calendarInfo?.nextCycleStartDate;

    if (!currentCycleDueDate || !currentCycleStartDate) {
      return {
        text: CCM_HOME_MESSAGES.UPDATE_STOCK_DETAILS_ALERT,
        isExpired: false
      };
    }

   
    // Check if current cycle start date is greater than or equal to due date
    const cycleStartDate = parseAndNormalizeDate(currentCycleStartDate);
    const cycleDueDate = parseAndNormalizeEndOfDay(currentCycleDueDate);
    const now = new Date();
    // Parse dates and remove time component for accurate comparison

    if (cycleStartDate <= now && now <= cycleDueDate) {
      return {
        text: (
          <>
            {CCM_HOME_MESSAGES.UPDATE_STOCK_DETAILS_BEFORE}{" "}
            <span className="alert-date">{formatCycleInfoDate(currentCycleDueDate, "DD MMM")}</span>
          </>
        ),
        isExpired: false
      };
    } else {
      return {
        text: (
          <>
            {CCM_HOME_MESSAGES.NEXT_STOCK_UPDATE_DUE_ON}{" "}
            <span className="alert-date">{formatCycleInfoDate(nextCycleStartDate, "DD MMM")}</span>
          </>
        ),
        isExpired: true
      };
    }
  };
  
  const SearchIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M15.755 14.255H14.965L14.685 13.985C15.665 12.845 16.255 11.365 16.255 9.755C16.255 6.165 13.345 3.255 9.755 3.255C6.165 3.255 3.255 6.165 3.255 9.755C3.255 13.345 6.165 16.255 9.755 16.255C11.365 16.255 12.845 15.665 13.985 14.685L14.255 14.965V15.755L19.255 20.745L20.745 19.255L15.755 14.255ZM9.755 14.255C7.26501 14.255 5.255 12.245 5.255 9.755C5.255 7.26501 7.26501 5.255 9.755 5.255C12.245 5.255 14.255 7.26501 14.255 9.755C14.255 12.245 12.245 14.255 9.755 14.255Z"
        fill="black"
        fill-opacity="0.56"
      />
    </svg>
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
       
       // Fetch user details first
        const userData = await fetchUserDetails();
        setUserDetails(userData);

        // Get country code from URL parameters or use default
        const urlParams = new URLSearchParams(window.location.search);
        const countryCode = urlParams.get('countryCode') || 'ID'; // Default to ID

        // Fetch product list with display names
        try {
          const productsResponse = await get('/api/v1/products/corn-seeds', { countryCode });
          if (productsResponse && Array.isArray(productsResponse)) {
            const products = productsResponse.map((item: any) => ({
              productName: item.product_name,
              productDisplayName: item.product_display_name || item.product_name,
              stockQuantity: 0
            }));
            setProductsData(products);
          } else {
            setProductsData([]);
          }
        } catch (error) {
          console.error('Error fetching products:', error);
          setProductsData([]);
        }

        // Fetch sales dashboard data based on logged-in user
        if (userData && userData.id) {
          console.log('Fetching product stock summary for user:', userData.id);
          try {
            const response = await get('/api/v1/customers/product-stock-summary');
            
            if (response && response.length > 0) {
              setSalesDashboardData(response);
              calculateSummaryByStatus(response);
            } else {
              console.warn('No sales dashboard data found for user:', userData.id);
              setSalesDashboardData([]);
            }
          } catch (error) {
            console.error('Failed to fetch product stock summary:', error);
            setSalesDashboardData([]);
          }

          // Fetch update history data
          await fetchUpdateHistory();
        } else {
          console.error('No valid user data available for API call');
          setSalesDashboardData([]);
        }
        
      } catch (error) {
        console.error('Error fetching CCM data:', error);
        setSalesDashboardData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Effect to check history for all customers when salesDashboardData changes
  useEffect(() => {
    if (salesDashboardData && salesDashboardData.length > 0) {
      checkHistoryForAllCustomers(salesDashboardData);
    }
  }, [salesDashboardData]);

  const refreshCCMDashboardData = async () => {
    console.log('Starting CCM dashboard data refresh...');
    setIsLoading(true);
    
    try {
      if (userDetails?.id) {
        console.log('Refreshing CCM dashboard data for user:', userDetails.id);
        
        // Fetch fresh sales dashboard data
        const response = await get('/api/v1/customers/product-stock-summary');
        console.log('CCM Refresh API response:', response);
        
        if (response && response.length > 0) {
          setSalesDashboardData(response);
          console.log('CCM dashboard data refreshed successfully:', response);
        } else {
          console.warn('No CCM dashboard data found during refresh:', userDetails.id);
          setSalesDashboardData([]);
        }
      } else {
        console.error('No user details available for CCM refresh');
      }
    } catch (error) {
      console.error('Error refreshing CCM dashboard data:', error);
      // Don't clear existing data on error, just log it
    } finally {
      setIsLoading(false);
      console.log('CCM dashboard refresh completed');
    }
  };

  const fetchUpdateHistory = async () => {
    try {
      const response = await get('/api/v1/customers/updated-by-user/product-stocks');
      
      if (response && Array.isArray(response)) {
        // Enrich the update history with productDisplayName and proper structure
        const enrichedHistory = response.map((item: any) => {
          // Map productStocks with enriched productDisplayName
          const enrichedProductStocks = item.productStocks?.map((product: any) => {
            // Find matching product from productsData to get productDisplayName
            const matchingProduct = productsData.find(p => p.productName === product.productName);
            return {
              productName: product.productName || 'Unknown Product',
              productDisplayName: product.productDisplayName || matchingProduct?.productDisplayName || product.productName,
              stockQuantity: product.stockQuantity || 0
            };
          }) || [];
          
          return {
            ...item,
            stockData: enrichedProductStocks, // Add stockData field for compatibility
            productStocks: enrichedProductStocks // Update productStocks with enriched data
          };
        });
        
        setAllUpdateHistory(enrichedHistory);
      } else {
        console.warn('No update history data found');
        setAllUpdateHistory([]);
      }
    } catch (error) {
      console.error('Error fetching update history:', error);
      setAllUpdateHistory([]);
    }
  };

  const handleBackClick = () => {
    console.log('Back button clicked');
  };

  const handleUpdateStock = async (retailerId: string) => {
    console.log('Update stock for retailer:', retailerId);
    setIsLoading(true);
    
    // Store the selected retailer ID
    setSelectedRetailerId(retailerId);
    console.log('Selected retailer ID set to:', retailerId);
  
    // Get country code from URL parameters or use default
    const urlParams = new URLSearchParams(window.location.search);
    const countryCode = urlParams.get('countryCode') || 'ID'; // Default to ID
    try {
      // Fetch corn seed products template same as in home.tsx    
      if (productsData && Array.isArray(productsData)) {
        setSelectedRetailerStockData(productsData);
        console.log('Corn seed products fetched for retailer:', productsData);
      } else {
        console.warn('No corn seed products found for country:', countryCode);
        setSelectedRetailerStockData([]);
      }
    } catch (error) {
      console.error('Error fetching corn seed products:', error);
      setSelectedRetailerStockData([]);
    } finally {
      setIsLoading(false);
    }
    setShowUpdateStockPage(true);
  };

  const handleViewHistory = async (retailerId: string) => {
    console.log('View history for retailer:', retailerId);
    setIsLoading(true);
    
    try {
      // Call the specific API endpoint for customer product stocks
      const response = await get(`/api/v1/customers/${retailerId}/product-stocks`);
      console.log('Customer product stocks API response:', response);
      
      if (response && (Array.isArray(response) ? response.length > 0 : response)) {
        // Handle the response data
        const historyData = Array.isArray(response) ? response : [response];
        
        // Convert API response to the format expected by UpdateHistoryList
        const formattedHistory = historyData.map((item: any) => {
          console.log('Raw API item:', item);
          const dateField = item.stockCycle || new Date().toISOString();
          console.log('Date field selected:', dateField);
          
          // Map productStocks with enriched productDisplayName
          const enrichedProductStocks = item.productStocks?.map((product: any) => {
            // Find matching product from productsData to get productDisplayName
            const matchingProduct = productsData.find(p => p.productName === product.productName);
            return {
              productName: product.productName || 'Unknown Product',
              productDisplayName: product.productDisplayName || matchingProduct?.productDisplayName,
              stockQuantity: product.stockQuantity || 0
            };
          }) || [];
          
          return {
            stockCycle: dateField, // Add stockCycle as well for compatibility
            totalAvailableStock: item.totalAvailableStock || 0,
            customerId: retailerId,
            stockData: enrichedProductStocks, // Use enriched product stocks for stockData
            productStocks: enrichedProductStocks // Also keep productStocks for fallback compatibility
          };
        });
        
        // Filter out entries with no product data to avoid showing empty history
        const filteredHistory = formattedHistory.filter((entry: any) => 
          entry.stockData && entry.stockData.length > 0
        );
        
        console.log('Formatted history data for retailer:', filteredHistory);
        setSelectedRetailerHistoryData(filteredHistory);
        setSelectedRetailerId(retailerId);

        // Find the specific customer from salesDashboardData to get the customer name
        const customerInfo = salesDashboardData?.find((customer: any) => customer.customerId === retailerId);

        // Set retailer info for display
        setSelectedRetailerInfo({
          customerName: customerInfo?.customerName || 'Unknown Customer',
          customerId: retailerId
        });
        
        setShowHistoryPage(true);
      } else {
        console.warn('No history data returned from API for retailer:', retailerId);
        
        // Fallback: try to find retailer info from salesDashboardData
        const retailerInfo = salesDashboardData?.find((r: any) => r.customerId === retailerId);
        setSelectedRetailerHistoryData([]);
        setSelectedRetailerId(retailerId);
        setSelectedRetailerInfo({
          customerName: retailerInfo?.customerName || 'Unknown Customer',
          customerId: retailerId
        });
        
        setShowHistoryPage(true);
      }
    } catch (error) {
      console.error('Error fetching customer product stocks:', error);
      
      // Fallback to local data if API fails
      const retailerInfo = salesDashboardData?.find((r: any) => r.customerId === retailerId);
      setSelectedRetailerHistoryData([]);
      setSelectedRetailerId(retailerId);
      setSelectedRetailerInfo({
        customerName: retailerInfo?.customerName || 'Unknown Customer',
        customerId: retailerId
      });
      
      setShowHistoryPage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackFromHistory = () => {
    setShowHistoryPage(false);
    setSelectedEntryFromCCM(null); // Clear selected entry when navigating back
  };

  const handleBackFromCCMUpdateHistory = () => {
    setShowCCMUpdateHistory(false);
    setSelectedEntryFromCCM(null); // Clear selected entry when navigating back from CCM Update History
  };

  const handleNavigateFromCCMHistory = (historyData: any[], retailerInfo: any) => {
    console.log('Navigating from CCM history to individual history:', retailerInfo);
    
    // Set the data for the individual history page
    setSelectedRetailerHistoryData(historyData);
    setSelectedRetailerId(retailerInfo.customerId);
    setSelectedRetailerInfo(retailerInfo);
    
    // Hide CCM history and show individual history
    setShowCCMUpdateHistory(false);
    setShowHistoryPage(true);
  };

  const handleBackFromUpdateStock = () => {
    setShowUpdateStockPage(false);
  };

  const handleSaveStockDetails = (updatedStockData: any[]) => {
    console.log('Stock details saved:', updatedStockData);
    setShowUpdateStockPage(false);
  };

  const handleViewAllHistory = () => {
    console.log('View all history clicked');
    
    // Use the allUpdateHistory from API instead of deriving from salesDashboardData
    const allCcmHistory = (allUpdateHistory || [])
      .sort(
        (a: any, b: any) =>
          new Date(b.stockCycle || 0).getTime() - new Date(a.stockCycle || 0).getTime()
      );
      // Remove .slice(0, 2) to show all history records
   
    console.log('All CCM history data from API:', allCcmHistory);
    setCcmHistoryData(allCcmHistory);
    setShowCCMUpdateHistory(true);
  };

  const handleCCMHistoryItemClick = async (historyItem: any) => {
    console.log('CCM history item clicked:', historyItem);
    
    // Set the clicked item as selected entry and prepare CCM Update History navigation
    setSelectedEntryFromCCM(historyItem);
    
    // Only set up CCM Update History (same as View More functionality) and let CCMUpdateHistory handle the detailed navigation
    const allCcmHistory = (allUpdateHistory || [])
      .sort(
        (a: any, b: any) =>
          new Date(b.stockCycle || 0).getTime() - new Date(a.stockCycle || 0).getTime()
      );
      // Remove .slice(0, 2) to show all history records
   
    console.log('All CCM history data for logged-in user:', allCcmHistory);
    setCcmHistoryData(allCcmHistory);
    setShowCCMUpdateHistory(true);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'status-pending';
      case 'COMPLETED': return 'status-completed';
      case 'DATA_NOT_AVAILABLE': return 'status-datanotavailable';
      default: return 'status-pending';
    }
  };

  // Method to get display name for subClassification
  const getSubClassificationDisplayName = (subClassification: string) => {
    const displayNameMap: { [key: string]: string } = {
      'R1-Regular': 'R1-Regular',
      'FIELD_OFFICER': 'Field Officer',
      'R2-Regular': 'R2-Regular',
      'R1': 'R1',
      'R2': 'R2',
      'R2-BLF': 'R2-BLF',
      'FO': 'Field Officer',
      'TM': 'Territory Manager'
    };

    return displayNameMap[subClassification] || toCamelCase(subClassification);
  };

  // Method to check history for all customers
  const checkHistoryForAllCustomers = async (customers: any[]) => {
    const historyChecks = await Promise.allSettled(
      customers.map(async (customer: any) => {
        try {
          const response = await get(`/api/v1/customers/${customer.customerId}/product-stocks`);
          return {
            customerId: customer.customerId,
            hasHistory: response && (Array.isArray(response) ? response.length > 0 : response)
          };
        } catch (error) {
          console.error('Error checking history for customer:', customer.customerId, error);
          return {
            customerId: customer.customerId,
            hasHistory: false
          };
        }
      })
    );

    const customersWithHistorySet = new Set<string>();
    historyChecks.forEach((result) => {
      if (result.status === 'fulfilled' && result.value.hasHistory) {
        customersWithHistorySet.add(result.value.customerId);
      }
    });
    
    setCustomersWithHistory(customersWithHistorySet);
  };

  // Method to calculate summation based on status
  const calculateSummaryByStatus = (data: any[] | any, status?: 'PENDING' | 'COMPLETED' | 'ALL') => {
    // Ensure data is always an array
    const customers = Array.isArray(data) ? data : (data ? [data] : []);

    let filteredCustomers = customers;
    if (status && status !== 'ALL') {
      filteredCustomers = customers.filter((customer: any) => 
        customer.status === status
      );
    }

    const totalStock = filteredCustomers.reduce((sum: number, customer: any) => 
      sum + (customer.totalAvailableStock || 0), 0
    );

    const totalUsers = filteredCustomers.length;

    return {
      totalStock,
      totalUsers,
      status: status || 'ALL'
    };
  };
  return (
    <>
      {showUpdateStockPage ? (
        <UpdateStockDetails
          stockData={selectedRetailerStockData}
          selectedDate={calendarInfo?.currentCycleStartDate}
          onBack={handleBackFromUpdateStock}
          onSave={handleSaveStockDetails}
          onNavigateToCCMHome={async () => {
            await refreshCCMDashboardData();
            await fetchUpdateHistory();
            setShowUpdateStockPage(false);
          }}
          loggedInUser={userDetails?.id}
          userRole="ccm"
          customerId={selectedRetailerId}
        />
      ) : showCCMUpdateHistory ? (
        <CCMUpdateHistory
          historyData={ccmHistoryData}
          onBack={handleBackFromCCMUpdateHistory}
          onNavigateToHome={() => setShowCCMUpdateHistory(false)}
          onNavigateToHistory={handleNavigateFromCCMHistory}
          userDetails={userDetails || undefined}
          selectedItem={selectedEntryFromCCM}
          productsData={productsData}
          onRefreshAndNavigateHome={async () => {
            // Hide the CCM Update History page first
            setShowCCMUpdateHistory(false);
            // Then refresh the dashboard data
            await refreshCCMDashboardData();
            await fetchUpdateHistory();
            console.log('CCM dashboard refreshed after save');
          }}
        />
      ) : showHistoryPage ? (
        <UpdateHistoryList
          data={selectedRetailerHistoryData}
          onBack={handleBackFromHistory}
          onNavigateToHome={async () => {
            // Hide history page immediately to avoid flickering
            setShowHistoryPage(false);
            setSelectedEntryFromCCM(null);
            // Then refresh data in background
            await refreshCCMDashboardData();
            await fetchUpdateHistory();
          }}
          onNavigateToCCMHome={async () => {
            // Hide history page immediately to avoid flickering
            setShowHistoryPage(false);
            setSelectedEntryFromCCM(null);
            // Then refresh data in background
            await refreshCCMDashboardData();
            await fetchUpdateHistory();
          }}
          retailerId={selectedRetailerId}
          retailerInfo={selectedRetailerInfo}
          userRole="ccm"
          selectedEntryFromHome={selectedEntryFromCCM}
        />
      ) : (
        <div className="page-container ccm-home-container">
          {isLoading && <Loader />}

          {/* Header Section */}
          <Header title="Corn Stock" onBackClick={handleBackClick} showBackIcon={false}/>

          <div className="ccm-content-section">
            {/* Date Section */}
            <div className="date-section">
              <p className="date-text">
                {formatCycleInfoDate(
                  new Date().toString(),
                  "DDDD, MMM DD YYYY"
                )}
              </p>
            </div>

            {/* Field Officer Card */}
            <div className="officer-card">
              <div className="officer-info">
                <h2 className="officer-name">
                  {userDetails?.name}
                </h2>
                <p className="officer-id">
                  ID : {userDetails?.id}
                </p>
              </div>
              <div className="officer-badge">
                <span className="badge-text">
                  {getSubClassificationDisplayName(userDetails?.subClassification || '')}
                </span>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">
                  {CCM_HOME_MESSAGES.STOCK_AS_OF}{" "}
                  {formatCycleInfoDate(
                    calendarInfo?.currentCycleStartDate,
                    "DD MMM"
                  )}
                </div>
                <div className="stat-value">
                  {calculateSummaryByStatus(salesDashboardData, 'ALL').totalStock} {CCM_HOME_MESSAGES.KGS}
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">{CCM_HOME_MESSAGES.RETAILERS}</div>
                <div className="stat-value">
                  {calculateSummaryByStatus(salesDashboardData,'COMPLETED').totalUsers}/
                  {calculateSummaryByStatus(salesDashboardData,'ALL').totalUsers} {CCM_HOME_MESSAGES.UPDATED}
                </div>
              </div>
            </div>

            {/* Corn Hybrid Section */}
            <div className="corn-section-header">
              <span className="corn-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="21"
                  viewBox="0 0 9 21"
                  fill="none"
                >
                  <path
                    d="M4.81184 0.430664C5.24527 0.725882 5.69645 1.16923 6.02348 1.58598C6.32652 1.97195 6.58158 2.42834 6.72919 2.89568C7.28259 2.70843 8.55839 2.32037 8.64758 3.22636C8.71643 3.92581 8.68722 5.29967 8.54118 5.9793C8.51301 6.11074 8.38053 6.39917 8.37948 6.48263C8.37844 6.53687 8.62254 6.73977 8.64758 7.0209C8.71069 7.73182 8.68983 9.01232 8.54848 9.70238C8.52031 9.84112 8.38053 10.1963 8.37948 10.2772C8.37844 10.3371 8.61889 10.5265 8.64602 10.7779C8.71643 11.4356 8.68931 12.8601 8.54848 13.4969C8.20267 15.0627 6.84915 16.363 5.24267 16.5482L5.23902 19.9672C5.20042 20.24 5.02256 20.3344 4.81236 20.4596H4.49941C4.27617 20.3281 4.11187 20.2092 4.07275 19.9281L4.0691 16.5482C2.18617 16.3046 0.78415 14.6898 0.664185 12.8136C0.629239 12.2655 0.616721 11.3548 0.664185 10.8154C0.6887 10.5406 0.793538 10.4769 0.939061 10.2704C0.783107 9.86615 0.6887 9.41394 0.664185 8.9805C0.635498 8.47404 0.602116 7.422 0.669401 6.94788C0.700696 6.72621 0.918197 6.57808 0.93228 6.50923C0.938539 6.47845 0.784671 6.01268 0.763286 5.90836C0.625588 5.2256 0.601073 3.96702 0.664185 3.26601C0.749725 2.31515 1.99162 2.70008 2.58206 2.8962C2.74949 2.40904 3.00767 1.95004 3.32688 1.54738C3.64609 1.14472 4.07536 0.713364 4.49888 0.430664H4.81184ZM5.66672 3.47516C5.53423 2.8503 5.14982 2.29116 4.66944 1.88067C4.15255 2.25934 3.81248 2.84822 3.63514 3.45638C4.04823 3.73335 4.3737 4.11828 4.63606 4.53815L5.16286 3.89139L5.66672 3.47516ZM4.06858 7.58943V6.31781C4.06858 6.1655 3.90793 5.68877 3.84117 5.52812C3.51048 4.73896 2.69837 4.03847 1.8388 3.91173V5.30019C1.8388 5.43059 1.94729 5.82334 1.99371 5.96678C2.03126 6.08309 2.20234 6.49462 2.28006 6.56034C2.35777 6.62606 2.96751 6.81644 3.1558 6.91711C3.49118 7.09601 3.78588 7.33803 4.06858 7.58891V7.58943ZM7.47193 3.91225C6.48196 4.07186 5.61299 4.92048 5.34855 5.87706C5.32351 5.96678 5.24214 6.29068 5.24214 6.35745V7.58943C5.55145 7.32864 5.86961 7.06315 6.23264 6.87799C6.40789 6.7888 6.95295 6.62763 7.04057 6.55148C7.0969 6.50245 7.232 6.20201 7.26746 6.11334C7.33579 5.94174 7.47245 5.5083 7.47245 5.34035V3.91225H7.47193ZM4.06858 11.384V10.1123C4.06858 9.98664 3.93349 9.53912 3.88028 9.4009C3.5376 8.51472 2.75783 7.88048 1.8388 7.66715V9.09525C1.8388 9.22252 1.94729 9.62205 1.99371 9.76184C2.02292 9.84946 2.23416 10.3199 2.27902 10.357C2.32387 10.394 2.63995 10.4613 2.73801 10.5046C3.22987 10.7216 3.6899 11.0011 4.06858 11.384ZM7.47193 9.13489C7.51678 8.66964 7.43854 8.14023 7.47193 7.66819C6.59462 7.88361 5.82215 8.48395 5.46956 9.3237C5.39236 9.50782 5.24214 9.96891 5.24214 10.1525V11.3845C5.7413 10.9005 6.35834 10.5447 7.03014 10.3559C7.19131 10.238 7.45106 9.35135 7.47193 9.13541V9.13489ZM4.06858 15.3741V13.9074C4.06858 13.8406 3.98721 13.5167 3.96217 13.427C3.69043 12.4438 2.83398 11.6625 1.8388 11.4622V12.8903C1.8388 13.0583 1.99423 13.5454 2.06621 13.7191C2.41411 14.5573 3.1897 15.1717 4.06858 15.3746V15.3741ZM7.47193 11.4622C6.55863 11.6505 5.76217 12.3233 5.43044 13.1954C5.37202 13.3483 5.24214 13.801 5.24214 13.946V15.3741C6.14657 15.1879 6.95347 14.5051 7.28363 13.6409C7.33318 13.5105 7.47193 13.0447 7.47193 12.9294V11.4627V11.4622Z"
                    fill="#007CBF"
                  />
                </svg>
              </span>
              <h3 className="corn-section-title">{CCM_HOME_TITLES.CORN_HYBRID_STOCK_OVERVIEW}</h3>
            </div>

            {/* Check if there are no retailers */}
            {calculateSummaryByStatus(salesDashboardData,'ALL').totalUsers === 0 ? (
              /* Show no retailers message when no retailers are linked */
              <div className="no-retailers-message">
                <p className="no-retailers-text">
                  <b>{CCM_HOME_MESSAGES.NO_RETAILERS_LINKED}</b><br />
                  {CCM_HOME_MESSAGES.ONCE_ASSIGNED_MESSAGE}
                </p>
              </div>
            ) : (
              <>
                {/* Update Stock Alert */}
                <div className={`update-alert ${getAlertMessage().isExpired ? 'alert-expired' : ''}`}>
                  <span className="alert-icon">
                    {getAlertMessage().isExpired ? (
                      // Warning/Error icon for expired state
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
    <path d="M12.5 2C6.98 2 2.5 6.48 2.5 12C2.5 17.52 6.98 22 12.5 22C18.02 22 22.5 17.52 22.5 12C22.5 6.48 18.02 2 12.5 2ZM13.5 17H11.5V11H13.5V17ZM13.5 9H11.5V7H13.5V9Z" fill="black" fill-opacity="0.56"/>
    </svg>
                    ) : (
                      // Info icon for normal state
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                      >
                        <path
                          d="M12.5 2C6.98 2 2.5 6.48 2.5 12C2.5 17.52 6.98 22 12.5 22C18.02 22 22.5 17.52 22.5 12C22.5 6.48 18.02 2 12.5 2ZM13.5 17H11.5V11H13.5V17ZM13.5 9H11.5V7H13.5V9Z"
                          fill="white"
                        />
                      </svg>
                    )}
                  </span>
                  <span className={`alert-text ${getAlertMessage().isExpired ? 'alert-expired' : ''}`}>
                    {getAlertMessage().text}
                  </span>
                </div>

                {/* Search Section */}
                <div className="search-section">
                  <div className="search-input-wrapper">
                    <TextField
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={CCM_HOME_MESSAGES.SEARCH_BY_RETAILER_ID}
                      prefixIcon={<SearchIcon />}
                    />
                  </div>
                </div>

                {/* Filter Buttons Section */}
                <div className="filter-section">
                  <div className="filter-buttons">
                    <button
                      className={`filter-btn ${selectedStatus === 'All' ? 'active' : ''}`}
                      onClick={() => setSelectedStatus('All')}
                    >
                      All
                    </button>
                    <button
                      className={`filter-btn ${selectedStatus === 'Pending' ? 'active' : ''}`}
                      onClick={() => setSelectedStatus('Pending')}
                    >
                      Pending
                    </button>
                    <button
                      className={`filter-btn ${selectedStatus === 'Updated' ? 'active' : ''}`}
                      onClick={() => setSelectedStatus('Updated')}
                    >
                      Updated
                    </button>
                  </div>
                </div>

                {/* Retailers List */}
                <div className="retailers-section">
                  {(() => {
                    const filteredRetailers = salesDashboardData
                      .filter((retailer: any) => {
                        // Search filter
                        if (searchQuery) {
                          const query = searchQuery.toLowerCase();
                          const matchesSearch = (
                            retailer.customerName?.toLowerCase().includes(query) ||
                            retailer.customerId?.toLowerCase().includes(query)
                          );
                          if (!matchesSearch) return false;
                        }

                        // Status filter
                        if (selectedStatus !== 'All') {
                          const retailerStatus = customersWithHistory.has(retailer.customerId) 
                            ? retailer.status 
                            : "DATA_NOT_AVAILABLE";
                          
                          if (selectedStatus === 'Pending') {
                            return retailerStatus === 'PENDING' || retailerStatus === 'DATA_NOT_AVAILABLE';
                          } else if (selectedStatus === 'Updated') {
                            return retailerStatus === 'COMPLETED';
                          }
                        }

                        return true;
                      })
                      .sort((a: any, b: any) => {
                        // First sort by status: PENDING first, then COMPLETED
                        const statusA = customersWithHistory.has(a.customerId) ? a.status : "DATA_NOT_AVAILABLE";
                        const statusB = customersWithHistory.has(b.customerId) ? b.status : "DATA_NOT_AVAILABLE";
                        
                        // Define status priority: PENDING = 0, COMPLETED = 1, DATA_NOT_AVAILABLE = 2
                        const getStatusPriority = (status: string) => {
                          switch (status?.toUpperCase()) {
                            case 'PENDING': return 0;
                            case 'DATA_NOT_AVAILABLE': return 1;
                            case 'COMPLETED': return 2;
                            default: return 3;
                          }
                        };
                        
                        const priorityA = getStatusPriority(statusA);
                        const priorityB = getStatusPriority(statusB);
                        
                        // If status priority is different, sort by status
                        if (priorityA !== priorityB) {
                          return priorityA - priorityB;
                        }
                        
                        // If status is the same, sort by customerName alphabetically
                        const nameA = a.customerName?.toLowerCase() || '';
                        const nameB = b.customerName?.toLowerCase() || '';
                        return nameA.localeCompare(nameB);
                      });

                    if (filteredRetailers.length === 0 && (searchQuery || selectedStatus !== 'All')) {
                      return (
                      /* Show no retailers message when no retailers are found */
                      <div className="empty-state">
                        <p className="no-retailers-found">
                          <b>{CCM_HOME_MESSAGES.NO_RETAILERS_FOUND}</b><br />
                          {searchQuery ? CCM_HOME_MESSAGES.NO_RETAILERS_FOUND_MESSAGE : `No retailers found with ${selectedStatus.toLowerCase()} status.`}
                        </p>
                      </div>
                      );
                    }

                    return filteredRetailers.map((retailer: any) => (
                    <div key={retailer.customerId} className="retailer-card">
                      <div className="retailer-header">

                           <span className='retailer-name-icon'>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M20 4H4V6H20V4ZM21 14V12L20 7H4L3 12V14H4V20H14V14H18V20H20V14H21ZM12 18H6V14H12V18Z"
                                fill="#007CBF"
                              />
                            </svg>
                          </span>


                        <div style={{width:'100%'}}>

                          <div className='retailer-name-container'>
                            <div className="retailer-name-section">
                         
                          <span className="retailer-name">
                            {retailer.customerName}
                          </span>
                        </div>
                        <div className="retailer-details">
                          <div className="retailer-quantity">
                            {retailer.totalAvailableStock} {CCM_HOME_MESSAGES.KGS}
                          </div>
                        </div>
                          </div>
                      <div className="retailer-info-row">
                        <p className="retailer-id">{CCM_HOME_MESSAGES.ID_PREFIX}{retailer.customerId}</p>
                        <div className="retailer-date">
                          {retailer.stockCycle && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M10.8333 2.5C6.69168 2.5 3.33334 5.85833 3.33334 10H0.833344L4.07501 13.2417L4.13334 13.3583L7.50001 10H5.00001C5.00001 6.775 7.60834 4.16667 10.8333 4.16667C14.0583 4.16667 16.6667 6.775 16.6667 10C16.6667 13.225 14.0583 15.8333 10.8333 15.8333C9.22501 15.8333 7.76668 15.175 6.71668 14.1167L5.53334 15.3C6.89168 16.6583 8.75834 17.5 10.8333 17.5C14.975 17.5 18.3333 14.1417 18.3333 10C18.3333 5.85833 14.975 2.5 10.8333 2.5ZM10 6.66667V10.8333L13.5667 12.95L14.1667 11.9417L11.25 10.2083V6.66667H10Z"
                                fill="black"
                                fill-opacity="0.56"
                              />
                            </svg>
                          )}
                          {retailer.stockCycle ? formatCycleInfoDate(retailer.stockCycle, "MMM DD") : ""}
                        </div>
                      </div>
                      <div className="retailer-status">
                        <span
                          className={`status-badge ${getStatusBadgeClass(
                            customersWithHistory.has(retailer.customerId) ? retailer.status : "DATA_NOT_AVAILABLE"
                          )}`}
                        >
                          {customersWithHistory.has(retailer.customerId) 
                            ? retailer.status === "COMPLETED" 
                              ? CCM_HOME_MESSAGES.UPDATED 
                              : toCamelCase(retailer.status)
                            : CCM_HOME_MESSAGES.DATA_NOT_AVAILABLE}
                        </span>
                      </div>


                        </div>


                       



                      </div>
                     
                      <div className="retailer-divider"></div>
                      <div className="retailer-actions">
                        {customersWithHistory.has(retailer.customerId) && (
                          <button
                            className="view-history-btn"
                            onClick={() => handleViewHistory(retailer.customerId)}
                          >
                            {CCM_HOME_MESSAGES.VIEW_HISTORY}
                          </button>
                        )}
                        {retailer.status === "PENDING" && 
                         calendarInfo?.currentCycleStartDate && 
                         calendarInfo?.currentCycleDueDate &&
                         parseAndNormalizeDate(calendarInfo.currentCycleStartDate) <= new Date() &&
                         new Date() <= parseAndNormalizeEndOfDay(calendarInfo.currentCycleDueDate)
                         && (
                          <button
                            className="update-stock-btn"
                            onClick={() => handleUpdateStock(retailer.customerId)}
                          >
                            {CCM_HOME_MESSAGES.UPDATE_STOCK}
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                  })()}
                </div>
                {/* Update History Section */}
                <div>
                  <h3 className="section-title">{CCM_HOME_TITLES.UPDATED_BY_YOU}</h3>
                  <div className="history-list">
                    {(() => {
                      // Get update history from API and display the latest 3
                      const displayHistory = (allUpdateHistory || [])
                        .sort(
                          (a: any, b: any) =>
                            new Date(b.stockCycle || 0).getTime() - new Date(a.stockCycle || 0).getTime()
                        )
                        .slice(0, 3);

                      if (displayHistory.length === 0) {
                        return (
                          <div className="ccm-empty-state">
                            <span className="ccm-empty-icon">📋</span>
                            <p className="ccm-empty-text">{CCM_HOME_MESSAGES.NO_UPDATE_HISTORY_FOUND}</p>
                          </div>
                        );
                      }

                      return displayHistory.map((item: any, index: number) => (
                        <div 
                          key={index} 
                          className="history-item clickable-history-item"
                          onClick={() => handleCCMHistoryItemClick(item)}
                        >
                          <div className="history-company-name">{item.customerName}</div>
                          <div className="history-quantity">
                            {item.totalAvailableStock} {CCM_HOME_MESSAGES.KGS}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                  {(() => {
                    // Only show "View More" button when there's data
                    return allUpdateHistory.length > 0 && (
                      <button className="view-more-btn" onClick={handleViewAllHistory}>
                        {CCM_HOME_MESSAGES.VIEW_MORE} {CCM_HOME_MESSAGES.VIEW_MORE_ARROW}
                      </button>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CCMHome;
